// Angular Files
import {
  Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, TemplateRef, ViewChild,
  DOCUMENT
} from '@angular/core';

import {
 NavigationEnd, Router, RouterEvent, Event
} from '@angular/router';
import { NgForm } from '@angular/forms';

// Other External Files
import { Subscription } from 'rxjs';

// Payment Integration Files
import { PaymentProcessorProvider } from 'apps/public-portal/src/app/payment-integrations';
import { ConvenienceFeeConfigModel } from 'apps/public-portal/src/app/payment-integrations/base/models';

// Teller Online Files
import { ItemDto } from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import {
    AuthService,
    CartModel,
    CartService,
    InboundRedirectService,
    DEFAULT_CONFIG_KEY
} from 'apps/public-portal/src/app/core/services';

// Teller Online Library Files
import {
    CartStatusEnum,
    TellerOnlineAppService,
    TellerOnlineFocusRestorationService,
    TellerOnlineSiteMetadataService
} from 'teller-online-libraries/core';
import {
    TellerOnlineDialogAction,
    TellerOnlineMessageService,
    TellerOnlineValidationService
} from 'teller-online-libraries/shared';

// Teller Online Files
import { cartPartitionHeading } from 'apps/public-portal/src/app/core/utils/cart-partition.utils';
import {
    AccountItemGroup,
    groupItemsByAccount,
    hasAccountGroupHeadings
} from 'apps/public-portal/src/app/core/utils/account-item-groups.utils';

@Component({
    selector: 'app-cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.scss'],
    host: {
        class: 'cart'
    },
    standalone: false
})
export class CartComponent implements OnInit, OnDestroy {
    // Declare @Output variables
    @Output() public toggleCart = new EventEmitter<boolean>();

    // Access html components
    @ViewChild('convenienceFeeNoteDialog') convenienceFeeNoteDialog: TemplateRef<any>;

    @ViewChild('disclaimerDialog') disclaimerDialog: TemplateRef<any>;

    // Public variables
    public showFeeNote: boolean = false;

    public showDisclaimer: boolean = false;

    public paymentPage: boolean = this.router.url.replace('#maincontent', '') === '/checkout';

    public get paymentRedirect(): boolean {
        // TODO (PROD-276): For now we only support either both or neither integration being a redirect.
        return this.paymentProvider.activeCreditProcessor?.redirect ?? false;
    }

    public cartDetails: CartDetails = {
        cartGuid: null,
        cartStatus: CartStatusEnum.Open,
        itemCount: 0,
        subtotal: {},
        convenienceFees: {},
        total: null,
        ver: 0
    };

    public disclaimer: string = this.siteMetadataService.customization.cartDisclaimer;

    /** A version of the disclaimer that can be used for the short preview text shown */
    public shortDisclaimer: string = this.disclaimer.replace(/<[^>]*>/g, '');

    public guestDetails: { email: string } = { email: null };

    // Private variables
    /** A hot copy of the items in the cart, not be used for anything other than price calculation */
    private _cartItems;

    // Subscriptions
    private _cartSubscription: Subscription;

    private _routerEventSubscription: Subscription;

    private _convenienceFeeSubscription: Subscription;

    private _cartTotalsSubscription: Subscription;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private el: ElementRef,
        private focusService: TellerOnlineFocusRestorationService,
        private messageService: TellerOnlineMessageService,
        private router: Router,
        private appService: TellerOnlineAppService,
        public cartService: CartService,
        public authService: AuthService,
        public validationService: TellerOnlineValidationService,
        public siteMetadataService: TellerOnlineSiteMetadataService,
        public paymentProvider: PaymentProcessorProvider,
        public inboundRedirectService: InboundRedirectService
    ) {
        this._routerEventSubscription = this.router.events.subscribe((event: Event | RouterEvent) => {
            if (event instanceof NavigationEnd) {
                this.paymentPage = event.urlAfterRedirects.replace('#maincontent', '') === '/checkout';
            }
        });
    }

    ngOnInit() {
        // Setup/listen for changes to the cart
        this._cartSubscription = this.cartService.cart$.subscribe(cart => {
            // Manually call the function so we can unit test if the function was called
            this._setupCart(cart);
        });

        // Sync totals from the service back to the local cartDetails for template bindings
        this._cartTotalsSubscription = this.cartService.cartTotals$.subscribe(totals => {
            this.cartDetails.subtotal = totals.subtotal;
            this.cartDetails.convenienceFees = totals.convenienceFees;
            this.cartDetails.total = totals.total;
        });

        // When the default config's fee changes (payment method switch), recompute
        // totals. The provider's useConvenienceFee fires default + all overrides
        // synchronously — cartService.calculateTotals awaits the HTTP call, so by
        // the time it reads feeConfigs all the per-override mutations have landed.
        this._convenienceFeeSubscription = this.paymentProvider.defaultConfig.convenienceFeeChanged$.subscribe(() => {
            this._calculateTotals();
        });
    }

    ngOnDestroy() {
        if (this._cartSubscription) this._cartSubscription.unsubscribe();
        if (this._routerEventSubscription) this._routerEventSubscription.unsubscribe();
        if (this._convenienceFeeSubscription) this._convenienceFeeSubscription.unsubscribe();
        if (this._cartTotalsSubscription) this._cartTotalsSubscription.unsubscribe();
    }

    async onClick_removeFromCart(item, button?: EventTarget) {
        // Safari (macOS and iOS) doesn't focus a button when it's clicked or tapped. Focus it,
        // so the prompt returns focus to it on close and the item it belongs to is found
        // below (QA 3.1.19).
        (button as HTMLElement)?.focus();

        // Save focus before the dialog captures it
        this.focusService.save('cart-remove');

        // Count the item's place among every item of its partition rather than within its own
        // list: the items are split into one list per account group, and the group the
        // removed item sat in can disappear along with it. The other partitions are left
        // out so focus never lands on another payment's item.
        const host = this.el.nativeElement as HTMLElement;
        const listItem = (
            this.document.activeElement as HTMLElement
        )?.closest('.item') as HTMLElement;
        const partitionKey = (
            listItem?.closest('.cart__details--items') as HTMLElement
        )?.dataset.partition;
        const partitionItems = () => Array.from(
            host.querySelectorAll<HTMLElement>('.cart__details--items')
        )
            .filter(list => list.dataset.partition === partitionKey)
            .flatMap(list => Array.from(list.querySelectorAll<HTMLElement>('.item')));
        const itemIndex = partitionItems().indexOf(listItem);

        this.cartService.removeItemFromCartPrompt(item).then(async confirm => {
            if (confirm) {
                await this.cartService.removeItemFromCart(item);

                this.messageService.notification(`${item.title} was removed from the cart.`, 'info', 4000);

                // Restore focus to the next (or previous)
                // delete button, or the cart heading
                setTimeout(() => {
                    this.focusService.restore(
                        'cart-remove',
                        () => {
                            // Query the DOM directly since
                            // Angular may have recreated the
                            // lists after the cart updated
                            const newItems = partitionItems();

                            const target = newItems[itemIndex]
                                ?? newItems[
                                    Math.max(0, itemIndex - 1)
                                ];

                            return (
                                target
                                    ?.querySelector<HTMLElement>(
                                        '.delete-button'
                                    )
                                ?? host.querySelector<HTMLElement>(
                                    '.sidebar-header h2'
                                )
                            );
                        }
                    );
                });
            }
        });
    }

    async onClick_displayWarning(item: ItemDto) {
        // Show a custom dialog with options to Resolve the conflict now or later
        const navigate: boolean = await this.messageService.custom({
            actions: [
                new TellerOnlineDialogAction({
                    text: 'Resolve Later',
                    close: false
                }),
                new TellerOnlineDialogAction({
                    text: 'Resolve Now',
                    close: true
                })
            ],
            content: item.warning.message,
            title: `Warning: ${item.title} Has Changed`
        });

        // Only route to item details if user selected 'Resolve Now'
        if (navigate) {
            this.router.navigate([`cart/${item.itemGuid}`]);
        }
    }

    onSubmit_proceedToPayment = (form: NgForm) => {
        this._proceedToPayment(form);
    };

    onClick_proceedToPayment = () => {
        this._proceedToPayment();
    };

    onClick_toggleCart = () => {
        this.toggleCart.emit(true);
    };

    onClick_showDisclaimer = () => {
        this.messageService.alert(this.disclaimer, 'Disclaimer', 'cart-disclaimer-modal');
    };

    getPartitionFeeConfig(partitionItems: ItemDto[]): ConvenienceFeeConfigModel {
        // A one-partition cart shows the cart-level fee as its partition's fee
        // (CartService.calculateTotals), so its label and note come from the default config
        // that holds that fee, as on checkout
        const defaultFeeConfig = this.paymentProvider.defaultConfig?.convenienceFee;
        if (defaultFeeConfig?.aggregateOverride != null && !this.hasMultiplePartitions()) {
            return defaultFeeConfig;
        }

        const configKey = partitionItems?.[0]?.page?.configKey || DEFAULT_CONFIG_KEY;
        return this._getFeeConfig(configKey);
    }

    /** Resolve a config's current convenience-fee config from the provider. When
     * an override has no explicit frontend fee config AND the default uses
     * plugin-driven fees, fall back to the default — the plugin computes the fee
     * per partition server-side, so the default acts as a display shell. We do
     * NOT fall back when the default is percent/flat — that would apply the
     * default's rate to an override that may have intentionally different fees. */
    private _getFeeConfig(configKey: string): ConvenienceFeeConfigModel {
        const defaultFeeConfig = this.paymentProvider.defaultConfig?.convenienceFee;
        if (configKey === DEFAULT_CONFIG_KEY) {
            return defaultFeeConfig;
        }
        const overrideFeeConfig = this.paymentProvider.configOverrides?.[configKey]?.convenienceFee;
        if (overrideFeeConfig) return overrideFeeConfig;

        return defaultFeeConfig?.isPluginDriven ? defaultFeeConfig : undefined;
    }

    onClick_setCartOpen = () => {
        this.messageService.prompt('If you have already started payment in another tab, you may be charged twice. Are you sure you want to unlock your cart?', 'Unlock Cart?', 'Cancel', 'Unlock').then(async confirm => {
            if (confirm) {
                this.cartService.openCart();
            }
        });
    };

    /**
     * The heading this payment shows. Shared with the checkout notices, which name the
     * payment a tender bound belongs to and have to say it in the same words the payer
     * reads here.
     */
    public getCartPartitionHeading(items: ItemDto[]) {
        return cartPartitionHeading(items) ?? 'Other';
    }

    /**
     * This payment's items gathered by account, in the order the accounts appear. Grouped one
     * payment at a time — the account numbers only mean anything within the response they
     * arrived in, so items of separate payments are never gathered together.
     */
    public getAccountGroups(items: ItemDto[]): AccountItemGroup<ItemDto>[] {
        return groupItemsByAccount(items);
    }

    /** Whether the payment holds more than one account, so its groups are worth heading. */
    public hasAccountGroupHeadings(groups: AccountItemGroup<ItemDto>[]): boolean {
        return hasAccountGroupHeadings(groups);
    }

    public hasMultiplePartitions() {
        return this.getPartitionsLength() > 1;
    }

    public getPartitionsLength() {
        return Object.keys(this.cartService.cartSnapshot.partitions).length;
    }

    /** Called when a new version of the cart is pushed (via a subscription) */
    private _setupCart(cart: CartModel) {
        this.appService.consoleLog(this, cart, `Event order: ${cart.version}`);

        // If this is an older version of the cart, we don't want it
        if (cart?.version && cart.version < this.cartDetails.ver) return;

        // Update the cart version
        this.cartDetails.ver = cart?.version;
        // Ensure the cart has finished loading before doing anything
        if (!this.cartService.loadingCart) {
            this.cartDetails.cartStatus = cart.cartStatus;
            this.cartDetails.cartGuid = cart.cartGuid;

            if (cart.guestEmailAddress) {
                this.guestDetails.email = cart.guestEmailAddress;
            }

            // Store the cart items for cost calculations later
            this._cartItems = cart.items;

            let changed = false;
            for (const key in this.cartDetails.subtotal) {
                // compare calculated subtotal with current subtotal for each partition
                if (cart.partitions[key]?.items.reduce((sub, item) => sub + item.amountInCart, 0) !== this.cartDetails.subtotal[key]) {
                    changed = true;
                    break;
                }
            }

            // If the subtotal hasn't changed, no need to re-calculate
            if (!changed && this.cartDetails.itemCount === this._cartItems?.length) return;

            if (this.cartDetails.cartGuid && this._cartItems?.length > 0) {
                this.cartDetails.itemCount = this._cartItems.length;
                this._calculateTotals();
            } else {
                this._resetTotals();
                this.cartDetails.itemCount = 0;
            }
        }
    }

    /** Delegate total calculation to CartService. Build feeConfigs fresh from the
     * provider so we never pass stale per-config references. */
    private _calculateTotals() {
        const feeConfigs = this.cartService.buildFeeConfigs(
            this.paymentProvider.defaultConfig,
            this.paymentProvider.configOverrides
        );

        this.cartService.calculateTotals(
            feeConfigs,
            () => this.paymentProvider.calculateConvenienceFee(this.cartDetails.cartGuid)
        );
    }

    /** Delegate total reset to CartService */
    private _resetTotals() {
        this.cartService.resetTotals();
    }

    /** Handles the act of trying to checkout - can be triggered via either enter in the form or clicking the button */
    private async _proceedToPayment(form: NgForm = null) {
        if (this.paymentRedirect) {
            this.appService.triggerLoading('Redirecting...');

            if (!this.authService.isSignedIn && this.cartDetails.cartStatus === CartStatusEnum.Open) {
                if (!this.validationService.runValidation(form, undefined, undefined, undefined, '.cart-form')) {
                    this.appService.finishLoading();
                    return false;
                }

                await this.cartService.updateCart({
                    guestEmailAddress: this.guestDetails.email
                });
            }

            const proceed = await this.cartService.refreshCart(this.cartDetails.cartGuid);
            if (!proceed) {
                this.appService.finishLoading();
                return false;
            }
        } else {
            // trigger a regular load while the processor decides what to do
            this.appService.triggerLoading();
        }

        // TODO (PROD-276): Currently we can't mix redirect & non-redirect processors,
        // so checkout only needs to be called on one of them.
        this.paymentProvider.activeCreditProcessor.checkout(this.cartDetails.cartGuid);
        return true;
    }
}

class CartDetails {
    cartGuid: string;

    cartStatus: CartStatusEnum;

    itemCount: number;

    subtotal: { [key: string]: number };

    convenienceFees: { [key: string]: number };

    total: { [key: string]: number };

    ver: number;
}
